import React, { useState } from 'react';
import { motion } from 'framer-motion';
import useTranslation from '../hooks/useTranslation';
import './Contact.css';

const Contact = () => {
  const { t, isRTL } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  
  const SHOW_FORM = false; // Flag to temporarily hide the form

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    // TODO: Replace with real API call when backend is ready.
    // Example:
    // const res = await fetch('https://api.orkina.com/contact', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(form),
    // });
    // const data = await res.json();

    await new Promise((r) => setTimeout(r, 1400)); // Simulated delay
    setSending(false);
    setSent(true);
    setForm({ name: '', email: '', message: '' });
    setTimeout(() => setSent(false), 6000);
  };

  return (
    <section id="contact" className="contact" aria-label={isRTL ? 'تواصل معنا' : 'Contact Us'}>
      <div className="contact__bg-shape" aria-hidden="true" />
      <div className="container">
        <motion.div
          className="contact__inner"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="contact__header">
            <div className="contact__label" aria-hidden="true">
              <span />
              <span>{isRTL ? 'ابدأ الحوار' : 'Start the Conversation'}</span>
            </div>
            <h2 className="contact__title">{t('contact.title')}</h2>
            <p className="contact__subtitle">{t('contact.subtitle')}</p>
          </div>

          <div className="contact__layout" style={!SHOW_FORM ? { gridTemplateColumns: '1fr' } : {}}>
            {/* Contact Details */}
            <div className="contact__info" style={!SHOW_FORM ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' } : {}}>
              <a href="tel:+971567114651" className="contact__info-card" style={{ textDecoration: 'none' }}>
                <div className="contact__info-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                  </svg>
                </div>
                <div>
                  <h4>{isRTL ? 'اتصل بنا' : 'Call Us'}</h4>
                  <p>0567114651</p>
                </div>
              </a>

              <a href="https://wa.me/971567114651" target="_blank" rel="noopener noreferrer" className="contact__info-card" style={{ textDecoration: 'none' }}>
                <div className="contact__info-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12c0 1.74.45 3.38 1.25 4.8L2 22l5.36-1.18A9.97 9.97 0 0012 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm4.35 14.65c-.2.57-1.16 1.09-1.58 1.13-.42.04-.94.13-3.13-.77-2.6-1.07-4.24-3.72-4.37-3.89-.13-.17-1.04-1.38-1.04-2.63 0-1.25.65-1.87.88-2.12.23-.25.5-.31.67-.31s.34 0 .49.01c.15.01.35-.06.54.4.19.46.65 1.58.71 1.71.06.13.1.29.02.46-.08.17-.12.27-.25.42-.12.15-.27.33-.38.46-.12.13-.25.27-.1.52.15.25.67 1.11 1.45 1.8 1 1 1.85 1.31 2.1 1.43.25.12.4.1.55-.07.15-.17.65-.75.82-1.01.17-.26.34-.22.57-.13.23.09 1.46.69 1.71.82.25.13.42.19.48.3.06.11.06.64-.14 1.21z" />
                  </svg>
                </div>
                <div>
                  <h4>{isRTL ? 'واتساب' : 'WhatsApp'}</h4>
                  <p>0567114651</p>
                </div>
              </a>

              <a 
                href="#email" 
                className="contact__info-card" 
                style={{ textDecoration: 'none' }}
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = `mailto:${['support', 'orkina.online'].join('@')}`;
                }}
              >
                <div className="contact__info-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <div>
                  <h4>{isRTL ? 'البريد الإلكتروني' : 'Email Us'}</h4>
                  <p>{['support', 'orkina.online'].join('@')}</p>
                </div>
              </a>

              <div className="contact__info-card">
                <div className="contact__info-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <h4>{isRTL ? 'موقعنا' : 'Our Location'}</h4>
                  <p>{isRTL ? 'الإمارات العربية المتحدة – الشارقة – المدينة الإعلامية (شمس)' : 'UAE - Sharjah - Media City (Shams)'}</p>
                </div>
              </div>
            </div>

            {/* Form */}
            {SHOW_FORM && (
            <form
              className="contact__form"
              onSubmit={handleSubmit}
              noValidate
              aria-label={isRTL ? 'نموذج التواصل' : 'Contact form'}
            >
              <div className="contact__row">
                <div className="contact__field">
                  <label htmlFor="contact-name" className="contact__field-label">
                    {t('contact.name')}
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    className="contact__input"
                    placeholder={t('contact.name')}
                    value={form.name}
                    onChange={handleChange}
                    required
                    autoComplete="name"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>
                <div className="contact__field">
                  <label htmlFor="contact-email" className="contact__field-label">
                    {t('contact.email')}
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    className="contact__input"
                    placeholder={t('contact.email')}
                    value={form.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    dir="ltr"
                  />
                </div>
              </div>

              <div className="contact__field">
                <label htmlFor="contact-message" className="contact__field-label">
                  {t('contact.message')}
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  className="contact__input contact__textarea"
                  placeholder={t('contact.message')}
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>

              {sent && (
                <motion.div
                  className="contact__success"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  role="status"
                  aria-live="polite"
                >
                  ✅ {t('contact.success')}
                </motion.div>
              )}

              <motion.button
                type="submit"
                className="btn-accent contact__submit"
                disabled={sending}
                whileHover={!sending ? { y: -3, boxShadow: '0 10px 36px rgba(200,168,107,0.5)' } : {}}
                whileTap={!sending ? { scale: 0.97 } : {}}
                id="contact-submit-btn"
              >
                {sending ? (
                  <>
                    <span className="contact__spinner" aria-hidden="true" />
                    {t('contact.sending')}
                  </>
                ) : (
                  t('contact.send')
                )}
              </motion.button>
            </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
